import { useId, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';

export interface IconTooltipButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'className'> {
  children: ReactNode;
  label: string;
  className?: string;
  /** `ghost`: fundo transparente; no hover só muda a cor do ícone. */
  variant?: 'default' | 'ghost';
}

export function IconTooltipButton({
  children,
  label,
  className,
  disabled,
  variant = 'default',
  ...props
}: IconTooltipButtonProps) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  function show(target: HTMLElement) {
    if (disabled) return;
    const rect = target.getBoundingClientRect();
    setPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + 8
    });
    setOpen(true);
  }

  function hide() {
    setOpen(false);
    setPosition(null);
  }

  return (
    <>
      <button
        aria-describedby={open ? tooltipId : undefined}
        aria-label={label}
        className={twMerge(
          'inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-[12px] transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50',
          variant === 'ghost'
            ? 'bg-transparent text-gray-5 hover:bg-transparent hover:text-brand-primary-3'
            : 'bg-body-light-mode-1 text-gray-5 hover:bg-gray-2',
          className
        )}
        disabled={disabled}
        onBlur={hide}
        onFocus={(event) => show(event.currentTarget)}
        onMouseEnter={(event) => show(event.currentTarget)}
        onMouseLeave={hide}
        type="button"
        {...props}
      >
        {children}
      </button>

      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <span
              className="pointer-events-none fixed z-[70] -translate-x-1/2 whitespace-nowrap rounded bg-brand-primary-3 px-3 py-1 text-[14px] font-normal leading-normal text-body-light-mode-1 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
              id={tooltipId}
              role="tooltip"
              style={{ left: position.left, top: position.top }}
            >
              {label}
            </span>,
            document.body
          )
        : null}
    </>
  );
}
